import React, { useMemo, useEffect } from 'react';
import { Modal, Heading, Text } from './ui';
import SignedImage from './SignedImage';

const colorTokens = {
  oxfordBlue: '#1b263b',
  alabaster: '#EEEEE7',
  white: '#FFFFFF'
};

// Helper to combine multi-component data with headers
const combineMultiComponentData = (recipe) => {
  if (!recipe.isMultiComponent || !recipe.components || recipe.components.length === 0) {
    return null;
  }

  // Parse components and assembly if they're JSON strings
  let components = recipe.components;
  if (typeof components === 'string') {
    try {
      components = JSON.parse(components);
    } catch (e) {
      return null;
    }
  }
  
  let assembly = recipe.assembly;
  if (typeof assembly === 'string') {
    try {
      assembly = JSON.parse(assembly);
    } catch (e) {
      assembly = null;
    }
  }

  const combinedIngredients = [];
  const combinedSteps = [];

  // Add all component ingredients with headers
  components.forEach((component) => {
    if (component.ingredients && Array.isArray(component.ingredients) && component.ingredients.length > 0) {
      const validIngredients = component.ingredients.filter(i => {
        const ing = typeof i === 'string' ? i : (i?.name || i?.text || String(i));
        return ing && ing.trim && ing.trim().length > 0;
      });
      if (validIngredients.length > 0) {
        combinedIngredients.push({
          type: 'header',
          text: `For the ${component.name}:`
        });
        validIngredients.forEach(ing => {
          const ingText = typeof ing === 'string' ? ing : (ing?.name || ing?.text || String(ing));
          combinedIngredients.push({
            type: 'item',
            text: ingText
          });
        });
      }
    }
  });

  // Add all component steps with headers
  components.forEach((component) => {
    if (component.steps && component.steps.length > 0) {
      const validSteps = component.steps
        .map(step => typeof step === 'string' ? step : step?.text || '')
        .filter(s => s && s.trim());
      if (validSteps.length > 0) {
        combinedSteps.push({
          type: 'header',
          text: `For the ${component.name}:`
        });
        validSteps.forEach(step => {
          combinedSteps.push({
            type: 'item',
            text: step
          });
        });
      }
    }
  });

  // Add assembly prerequisites with header
  if (assembly && assembly.prerequisites && Array.isArray(assembly.prerequisites) && assembly.prerequisites.length > 0) {
    const validPrereqs = assembly.prerequisites
      .map(p => typeof p === 'string' ? p : p?.text || '')
      .filter(p => p && p.trim());
    if (validPrereqs.length > 0) {
      combinedSteps.push({
        type: 'header',
        text: 'Assembly:'
      });
      validPrereqs.forEach(prereq => {
        combinedSteps.push({
          type: 'item',
          text: prereq
        });
      });
    }
  }

  // Add assembly steps at the end (without header)
  if (assembly && assembly.steps && assembly.steps.length > 0) {
    const validAssemblySteps = assembly.steps
      .map(step => typeof step === 'string' ? step : step?.text || '')
      .filter(s => s && s.trim());
    validAssemblySteps.forEach(step => {
      combinedSteps.push({
        type: 'item',
        text: step
      });
    });
  }

  return {
    ingredients: combinedIngredients,
    steps: combinedSteps
  };
};

// Parse ingredients from various formats (handles both simple and multi-component)
const parseIngredients = (recipe) => {
  // Check if this is a multi-component recipe
  const combinedData = combineMultiComponentData(recipe);
  if (combinedData) {
    // Return flat array with headers as strings (for diff comparison)
    return combinedData.ingredients.map(item => item.text);
  }

  // Simple recipe parsing
  const ingredients = recipe.ingredients;
  if (!ingredients) return [];
  if (Array.isArray(ingredients)) {
    return ingredients.map(item => {
      if (typeof item === 'string') return item;
      if (typeof item === 'object') return item.name || item.ingredient || JSON.stringify(item);
      return String(item);
    });
  }
  if (typeof ingredients === 'string') {
    try {
      const parsed = JSON.parse(ingredients);
      return parseIngredients({ ingredients: parsed });
    } catch {
      return ingredients.split(/\n|,|;/).map(s => s.trim()).filter(Boolean);
    }
  }
  return [];
};

// Parse steps/prerequisites from instructions object or array (handles both simple and multi-component)
const parseSteps = (recipe, field = 'steps') => {
  // Check if this is a multi-component recipe
  const combinedData = combineMultiComponentData(recipe);
  if (combinedData && field === 'steps') {
    // Return flat array with headers as strings (for diff comparison)
    return combinedData.steps.map(item => item.text);
  }
  
  if (combinedData && field === 'prerequisites') {
    // For prerequisites, combine component prerequisites and assembly prerequisites
    const prereqs = [];
    if (recipe.components) {
      let components = recipe.components;
      if (typeof components === 'string') {
        try {
          components = JSON.parse(components);
        } catch (e) {
          components = [];
        }
      }
      components.forEach((component) => {
        if (component.prerequisites && Array.isArray(component.prerequisites)) {
          component.prerequisites.forEach(p => {
            const pText = typeof p === 'string' ? p : (p?.text || String(p));
            if (pText && pText.trim()) {
              prereqs.push(pText.trim());
            }
          });
        }
      });
    }
    if (recipe.assembly) {
      let assembly = recipe.assembly;
      if (typeof assembly === 'string') {
        try {
          assembly = JSON.parse(assembly);
        } catch (e) {
          assembly = null;
        }
      }
      if (assembly && assembly.prerequisites && Array.isArray(assembly.prerequisites)) {
        assembly.prerequisites.forEach(p => {
          const pText = typeof p === 'string' ? p : (p?.text || String(p));
          if (pText && pText.trim()) {
            prereqs.push(pText.trim());
          }
        });
      }
    }
    return prereqs;
  }

  // Simple recipe parsing
  // First check if field exists directly on recipe
  if (recipe[field]) {
    if (Array.isArray(recipe[field])) {
      return recipe[field].map(s => String(s).trim()).filter(Boolean);
    }
    if (typeof recipe[field] === 'string') {
      try {
        const parsed = JSON.parse(recipe[field]);
        if (Array.isArray(parsed)) return parsed.map(s => String(s).trim()).filter(Boolean);
      } catch {
        return recipe[field].split('\n').map(s => s.trim()).filter(Boolean);
      }
    }
  }
  
  // Check in instructions object
  if (recipe.instructions) {
    let instructions = recipe.instructions;
    if (typeof instructions === 'string') {
      try {
        instructions = JSON.parse(instructions);
      } catch {
        return [];
      }
    }
    
    if (instructions[field]) {
      if (Array.isArray(instructions[field])) {
        return instructions[field].map(s => String(s).trim()).filter(Boolean);
      }
      if (typeof instructions[field] === 'string') {
        return instructions[field].split('\n').map(s => s.trim()).filter(Boolean);
      }
    }
  }
  
  return [];
};

// Compute what changed between arrays
const getArrayDiff = (original, updated) => {
  const origSet = new Set(original);
  const updSet = new Set(updated);
  
  // Find exact matches (unchanged)
  const unchanged = original.filter(item => updSet.has(item));
  
  // Items not matched yet
  const unmatchedOriginal = original.filter(item => !updSet.has(item));
  const unmatchedUpdated = updated.filter(item => !origSet.has(item));
  
  // Find items that were changed (similar but not identical)
  const changed = [];
  const matchedOriginal = new Set();
  const matchedUpdated = new Set();
  
  // For each unmatched updated item, try to find a similar original item
  unmatchedUpdated.forEach(updItem => {
    const updWords = updItem.toLowerCase().split(/\s+/).filter(w => w.length > 1); // Filter out single characters
    
    // Find the most similar original item
    let bestMatch = null;
    let bestScore = 0;
    
    unmatchedOriginal.forEach(origItem => {
      if (matchedOriginal.has(origItem)) return; // Already matched
      
      const origWords = origItem.toLowerCase().split(/\s+/).filter(w => w.length > 1);
      
      // Calculate similarity: count common words
      const commonWords = updWords.filter(w => origWords.includes(w));
      const totalWords = new Set([...updWords, ...origWords]).size;
      const similarity = commonWords.length / Math.max(updWords.length, origWords.length, 1);
      
      // Consider it a change if at least 40% of words match and there's at least 2 common words
      if (similarity >= 0.4 && commonWords.length >= 2 && similarity > bestScore) {
        bestMatch = origItem;
        bestScore = similarity;
      }
    });
    
    if (bestMatch) {
      changed.push(updItem);
      matchedOriginal.add(bestMatch);
      matchedUpdated.add(updItem);
    }
  });
  
  // Remaining unmatched items
  const added = unmatchedUpdated.filter(item => !matchedUpdated.has(item));
  const removed = unmatchedOriginal.filter(item => !matchedOriginal.has(item));
  
  return { added, removed, unchanged, changed };
};

const RecipeDiffModal = ({ isOpen, onClose, originalRecipe = {}, updatedRecipe = {} }) => {
  const parsedData = useMemo(() => {
    const origIngredients = parseIngredients(originalRecipe);
    const updIngredients = parseIngredients(updatedRecipe);
    const origSteps = parseSteps(originalRecipe, 'steps');
    const updSteps = parseSteps(updatedRecipe, 'steps');
    const origPrereqs = parseSteps(originalRecipe, 'prerequisites');
    const updPrereqs = parseSteps(updatedRecipe, 'prerequisites');
    
    const ingredientsDiff = getArrayDiff(origIngredients, updIngredients);
    const stepsDiff = getArrayDiff(origSteps, updSteps);
    const prerequisitesDiff = getArrayDiff(origPrereqs, updPrereqs);
    
    return {
      ingredients: {
        original: origIngredients,
        updated: updIngredients,
        diff: ingredientsDiff,
        hasChanges: JSON.stringify(origIngredients) !== JSON.stringify(updIngredients)
      },
      steps: {
        original: origSteps,
        updated: updSteps,
        diff: stepsDiff,
        hasChanges: JSON.stringify(origSteps) !== JSON.stringify(updSteps)
      },
      prerequisites: {
        original: origPrereqs,
        updated: updPrereqs,
        diff: prerequisitesDiff,
        hasChanges: JSON.stringify(origPrereqs) !== JSON.stringify(updPrereqs)
      },
      description: {
        original: originalRecipe.description || '',
        updated: updatedRecipe.description || '',
        hasChanges: originalRecipe.description !== updatedRecipe.description
      },
      name: {
        original: originalRecipe.name || '',
        updated: updatedRecipe.name || '',
        hasChanges: originalRecipe.name !== updatedRecipe.name
      }
    };
  }, [originalRecipe, updatedRecipe]);

  const RecipeColumn = ({ recipe, data, side }) => {
    // Only show circles on the updated recipe side
    const showCircles = side === 'updated';
    
    return (
      <div className="flex flex-col">
        {/* Header */}
        <div className="p-3 md:p-4 bg-neutral-50 hairline-b sticky top-0 z-10">
          <Heading level={3} className="text-neutral-900" style={{ color: colorTokens.oxfordBlue }}>
            {side === 'original' ? 'Original Recipe' : 'Updated Recipe'}
          </Heading>
        </div>

        <div className="p-4 pb-8 space-y-6 overflow-auto">
          {/* Recipe Preview Card */}
          <div className="bg-neutral-50 hairline p-3">
            <div className="aspect-square w-full mb-2 bg-neutral-0 hairline flex items-center justify-center overflow-hidden">
              <SignedImage
                src={recipe.images && recipe.images.length > 0 ? recipe.images[0] : null}
                alt={recipe.name}
                className="w-full h-full object-cover"
                category={recipe.category}
              />
            </div>
            <Heading level={4} className="text-neutral-900 line-clamp-2">{recipe.name}</Heading>
          </div>

          {/* Name */}
          {data.name.hasChanges && (
            <div className="p-3 hairline relative">
              <div className="font-mono uppercase text-xs tracking-wider text-neutral-900 mb-1">NAME</div>
              <div className={`text-base font-medium text-neutral-900 relative inline-block pr-20 ${showCircles ? 'hairline p-2' : ''}`} style={showCircles ? { border: '1px solid rgba(13, 13, 13, 0.9)', backgroundColor: '#F9F8F6' } : {}}>
                {data.name[side]}
                {showCircles && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 font-mono uppercase text-[10px] tracking-wider text-neutral-900 opacity-60">
                    CHANGED
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Description - only show if either recipe has one */}
          {(data.description.original || data.description.updated) && (
            <div className="p-3 hairline relative">
              <div className="font-mono uppercase text-xs tracking-wider text-neutral-900 mb-1">DESCRIPTION</div>
              <div className={`text-sm text-neutral-900 relative inline-block pr-20 ${data.description.hasChanges ? 'text-base font-medium' : ''} ${showCircles && data.description.hasChanges ? 'hairline p-2' : ''}`} style={showCircles && data.description.hasChanges ? { border: '1px solid rgba(13, 13, 13, 0.9)', backgroundColor: '#F9F8F6' } : {}}>
                {data.description[side] || '—'}
                {showCircles && data.description.hasChanges && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 font-mono uppercase text-[10px] tracking-wider text-neutral-900 opacity-60">
                    CHANGED
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Ingredients */}
          <div className="p-3 hairline">
            <div className="font-mono uppercase text-xs tracking-wider text-neutral-900 mb-2">
              INGREDIENTS {data.ingredients.hasChanges && `(${data.ingredients[side].length})`}
            </div>
            {data.ingredients[side].length === 0 ? (
              <div className="text-sm text-neutral-900 opacity-60">None listed</div>
            ) : (
              <ul className="space-y-1">
                {data.ingredients[side].map((ingredient, idx) => {
                  const isHeader = ingredient.startsWith('For the ') && ingredient.endsWith(':');
                  const isAdded = showCircles && data.ingredients.diff.added.includes(ingredient);
                  const isChanged = showCircles && data.ingredients.diff.changed.includes(ingredient);
                  const isRemoved = data.ingredients.diff.removed.includes(ingredient);
                  const hasModification = isAdded || isChanged;
                  
                  if (isHeader) {
                    return (
                      <li key={idx} className="mt-3 first:mt-0">
                        <div className="text-sm font-mono font-medium text-neutral-900" style={{ color: colorTokens.oxfordBlue }}>
                          {ingredient}
                        </div>
                      </li>
                    );
                  }
                  
                  return (
                    <li 
                      key={idx} 
                      className="text-sm flex items-start gap-2 relative"
                    >
                      <span className="text-neutral-900 opacity-60">•</span>
                      <span className={`flex-1 relative inline-block pr-20 ${hasModification ? 'hairline p-2' : ''}`} style={hasModification ? { border: '1px solid rgba(13, 13, 13, 0.9)', backgroundColor: '#F9F8F6' } : {}}>
                        {ingredient}
                        {isAdded && (
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 font-mono uppercase text-[10px] tracking-wider text-neutral-900 opacity-60">
                            ADDED
                          </span>
                        )}
                        {isChanged && (
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 font-mono uppercase text-[10px] tracking-wider text-neutral-900 opacity-60">
                            CHANGED
                          </span>
                        )}
                        {isRemoved && !showCircles && (
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 font-mono uppercase text-[10px] tracking-wider text-neutral-900 opacity-60">
                            REMOVED
                          </span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Prerequisites - only show if either recipe has any */}
          {(data.prerequisites.original.length > 0 || data.prerequisites.updated.length > 0) && (
            <div className="p-3 hairline">
              <div className="font-mono uppercase text-xs tracking-wider text-neutral-900 mb-2">
                PREREQUISITES {data.prerequisites.hasChanges && `(${data.prerequisites[side].length})`}
              </div>
              {data.prerequisites[side].length === 0 ? (
                <div className="text-sm text-neutral-900 opacity-60">None listed</div>
              ) : (
                <ol className="space-y-1 list-decimal list-inside">
                  {data.prerequisites[side].map((prereq, idx) => {
                    const isAdded = showCircles && data.prerequisites.diff.added.includes(prereq);
                    const isChanged = showCircles && data.prerequisites.diff.changed.includes(prereq);
                    const isRemoved = data.prerequisites.diff.removed.includes(prereq);
                    const hasModification = isAdded || isChanged;
                    
                    return (
                      <li 
                        key={idx} 
                        className={`text-sm relative inline-block pr-20 ${hasModification ? 'hairline p-2' : ''}`}
                        style={hasModification ? { border: '1px solid rgba(13, 13, 13, 0.9)', backgroundColor: '#F9F8F6' } : {}}
                      >
                        {prereq}
                        {isAdded && (
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 font-mono uppercase text-[10px] tracking-wider text-neutral-900 opacity-60">
                            ADDED
                          </span>
                        )}
                        {isChanged && (
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 font-mono uppercase text-[10px] tracking-wider text-neutral-900 opacity-60">
                            CHANGED
                          </span>
                        )}
                        {isRemoved && !showCircles && (
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 font-mono uppercase text-[10px] tracking-wider text-neutral-900 opacity-60">
                            REMOVED
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          )}

          {/* Steps */}
          <div className="p-3 hairline">
            <div className="font-mono uppercase text-xs tracking-wider text-neutral-900 mb-2">
              STEPS {data.steps.hasChanges && `(${data.steps[side].length})`}
            </div>
            {data.steps[side].length === 0 ? (
              <div className="text-sm text-neutral-900 opacity-60">None listed</div>
            ) : (
              <div className="space-y-2">
                {data.steps[side].map((step, idx) => {
                  const isHeader = (step.startsWith('For the ') && step.endsWith(':')) || step.startsWith('Assembly:');
                  const isAdded = showCircles && data.steps.diff.added.includes(step);
                  const isChanged = showCircles && data.steps.diff.changed.includes(step);
                  const isRemoved = data.steps.diff.removed.includes(step);
                  const hasModification = isAdded || isChanged;
                  
                  if (isHeader) {
                    return (
                      <div key={idx} className="mt-3 first:mt-0">
                        <div className="text-sm font-mono font-medium text-neutral-900" style={{ color: colorTokens.oxfordBlue }}>
                          {step}
                        </div>
                      </div>
                    );
                  }
                  
                  // Count step number (excluding headers)
                  const stepNumber = data.steps[side]
                    .slice(0, idx + 1)
                    .filter(s => !s.startsWith('For the ') && !s.startsWith('Assembly:') && !s.endsWith(':'))
                    .length;
                  
                  return (
                    <div key={idx} className="flex gap-2">
                      <span className="font-mono uppercase tracking-wider opacity-60 flex-shrink-0 text-sm">
                        {stepNumber}.
                      </span>
                      <div className={`flex-1 text-sm relative inline-block pr-20 ${hasModification ? 'hairline p-2' : ''}`}
                      style={hasModification ? { border: '1px solid rgba(13, 13, 13, 0.9)', backgroundColor: '#F9F8F6' } : {}}
                    >
                      {step}
                      {isAdded && (
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 font-mono uppercase text-[10px] tracking-wider text-neutral-900 opacity-60">
                          ADDED
                        </span>
                      )}
                      {isChanged && (
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 font-mono uppercase text-[10px] tracking-wider text-neutral-900 opacity-60">
                          CHANGED
                        </span>
                      )}
                      {isRemoved && !showCircles && (
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 font-mono uppercase text-[10px] tracking-wider text-neutral-900 opacity-60">
                          REMOVED
                        </span>
                      )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Add class to body when modal is open to hide header tab
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('compare-modal-open');
    } else {
      document.body.classList.remove('compare-modal-open');
    }
    return () => {
      document.body.classList.remove('compare-modal-open');
    };
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="max-w-6xl" className="p-0" hideCloseButton>
      <div className="p-6 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-h-[85vh]">
          {/* Original Recipe */}
          <div className="hairline md:border-r border-b md:border-b-0 overflow-auto">
            <RecipeColumn recipe={originalRecipe} data={parsedData} side="original" />
          </div>

          {/* Updated Recipe */}
          <div className="hairline overflow-auto">
            <RecipeColumn recipe={updatedRecipe} data={parsedData} side="updated" />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default RecipeDiffModal;
